// src/pages/Dashboard/index.jsx

import React, { useEffect, useState, useMemo } from 'react';
import VersionInfo from "components/VersionInfo";
import { Link } from 'react-router-dom';
import { supabase } from 'services/supabaseClient';
import { useProfile } from 'context/ProfileContext';
import { useAppAccess } from 'context/AppAccessContext';
import Page from 'components/Page';
import SectionCard from 'components/SectionCard';
import CalendarEventsTable from 'components/CalendarEventsTable';
import { getDashboardAppDestination } from './appDestination';
import { getMaturityReminderStatus, loadMaturityMessages } from './maturityMessages';
import {
  ShieldCheck,
  Upload,
  Users,
  KeyRound,
  Activity,
  LineChart,
  HelpCircle,
  LifeBuoy,
  BookOpenCheck,
  Repeat,
  WalletCards,
  CalendarDays,
} from 'lucide-react';

/* ---------------------- App catalog ---------------------- */

const APP_CATALOG = [
  {
    slug: 'market-map',
    label: 'MarketMap',
    to: '/market-map',
    requestTo: '/request-access?app=market-map',
    blurb: 'Interactive market analytics',
    is_paid: false,
    icon: <Activity className="h-5 w-5 text-brand" />,
  },
  {
    slug: 'trade-tracker',
    label: 'TradeTracker',
    to: '/trade-tracker',
    requestTo: '/request-access?app=trade-tracker',
    blurb: 'Track trade positions',
    is_paid: true,
    icon: <LineChart className="h-5 w-5 text-brand" />,
  },
  {
    slug: 'debt-studio',
    label: 'DebtStudio',
    to: '/debt-studio',
    requestTo: '/request-access?app=debt-studio',
    blurb: 'Debt issue structurer',
    is_paid: true,
    icon: <BookOpenCheck className="h-5 w-5 text-brand" />,
  },
  {
    slug: 'fund-planner',
    label: 'FundPlanner',
    to: '/fund-planner/funds',
    requestTo: '/request-access?app=fund-planner',
    blurb: 'Investment fund planner',
    is_paid: true,
    icon: <WalletCards className="h-5 w-5 text-brand" />,
  },
  {
    slug: 'capital-flow',
    label: 'CapitalFlow',
    to: '/capital-flow/overview',
    requestTo: '/request-access?app=capital-flow',
    blurb: 'Capital allocation reporting',
    is_paid: true,
    icon: <Repeat className="h-5 w-5 text-brand" />,
  },
];

/* ---------------------- Helpers ---------------------- */

function getDisplayName(profile) {
  if (!profile) return '';
  const { full_name, first_name, last_name, email } = profile;
  if (first_name && last_name) return `${first_name} ${last_name}`;
  if (first_name) return first_name;
  if (full_name) return full_name;
  if (email) return email.split('@')[0];
  return '';
}

const muted = 'text-muted';

/* ---------------------- Dashboard ---------------------- */

export default function Dashboard() {
  const { profile, selectedOrg, selectOrg, loading: profileLoading } = useProfile();
  const { apps = [], loading: accessLoading } = useAppAccess();

  const appSlugs = useMemo(
    () =>
      (apps || [])
        .map((a) => (typeof a === 'string' ? a : a?.slug))
        .filter(Boolean),
    [apps]
  );

  const isAdmin = !!profile?.is_admin;

  const hasDashboardAppAccess = (slug) => {
    if (slug === 'fund-planner') {
      return (
        appSlugs.includes('fund-planner') ||
        appSlugs.includes('debt-studio')
      );
    }

    return appSlugs.includes(slug);
  };

  const [adminSnap, setAdminSnap] = useState({
    missingSecurities: null,
    failedUploads: null,
    newUsers: null,
    userInvites: null,
    usersWithout2FA: null,
  });

  const [hasMfa, setHasMfa] = useState(true);
  const [maturityMessages, setMaturityMessages] = useState([]);
  const [maturityError, setMaturityError] = useState(false);
  const [maturitiesLoading, setMaturitiesLoading] = useState(true);
  const [calendarLookbackDays, setCalendarLookbackDays] = useState(7);
  const [calendarLookaheadDays, setCalendarLookaheadDays] = useState(14);
  const showMaturities = !isAdmin && appSlugs.includes('fund-planner');

  useEffect(() => {
    if (!showMaturities || !selectedOrg?.id) return;
    let cancelled = false;
    setMaturitiesLoading(true);
    setMaturityError(false);
    setMaturityMessages([]);
    loadMaturityMessages(selectedOrg.id, {
      lookbackDays: calendarLookbackDays,
      lookaheadDays: calendarLookaheadDays,
    })
      .then((messages) => {
        if (!cancelled) {
          setMaturityMessages(messages);
          setMaturitiesLoading(false);
        }
      })
      .catch((error) => {
        console.error('[Dashboard] Could not load FundPlanner maturities:', error);
        if (!cancelled) {
          setMaturityError(true);
          setMaturitiesLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [
    showMaturities,
    selectedOrg?.id,
    calendarLookbackDays,
    calendarLookaheadDays,
  ]);

  /* ---------- Admin snapshot ---------- */

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;

    Promise.all([
      supabase.rpc('get_admin_snapshot'),
      supabase.rpc('fp_security_master_missing'),
    ]).then(([snapshotResult, missingSecuritiesResult]) => {
      if (cancelled) return;

      const data = snapshotResult.data;
      setAdminSnap({
        missingSecurities: missingSecuritiesResult.error
          ? null
          : (missingSecuritiesResult.data || []).length,
        failedUploads: data ? (data.failedUploads ?? 0) : null,
        newUsers: data ? (data.newUsers ?? 0) : null,
        userInvites: data ? (data.userInvites ?? 0) : null,
        usersWithout2FA: data ? (data.usersWithout2FA ?? 0) : null,
      });

      if (snapshotResult.error) {
        console.error('[Dashboard] Could not load admin snapshot:', snapshotResult.error);
      }
      if (missingSecuritiesResult.error) {
        console.error(
          '[Dashboard] Could not load missing Security Master securities:',
          missingSecuritiesResult.error
        );
      }
    });

    return () => { cancelled = true; };
  }, [isAdmin]);

  /* ---------- MFA check ---------- */

  useEffect(() => {
    supabase.auth.mfa.listFactors().then(({ data }) => {
      const totp = data?.totp?.[0];
      setHasMfa(!!(totp && totp.status === 'verified'));
    });
  }, []);

  if (profileLoading || accessLoading) {
    return <div className="p-6">Loading…</div>;
  }

  const securitySection = !hasMfa ? (
    <SectionCard title="Security Reminder">
      <div className="text-sm text-muted">
        You have not enabled two-factor authentication (2FA).
      </div>
      <Link to="/settings" className="mt-2 inline-block btn btn-primary">
        Set up 2FA now
      </Link>
    </SectionCard>
  ) : (
    <SectionCard title="Security">
      <div className="text-sm text-muted">
        Two-factor authentication is enabled on your account.
      </div>
    </SectionCard>
  );
  const calendarEvents = maturityMessages.map((message) => {
    const reminderStatus = getMaturityReminderStatus(
      message.eventDate,
      new Date(),
      calendarLookaheadDays
    );
    return {
      id: `fund-planner-${message.id}`,
      status: reminderStatus.label,
      statusTone: reminderStatus.tone,
      date: message.eventDate,
      app: 'FundPlanner',
      title: message.eventType === 'interest' ? 'Interest Payment' : 'Principal Payment',
      details: `${message.fundName} · ${message.investmentType} · ${message.identifier}`,
      to: '/fund-planner/funds',
    };
  });

  return (
    <Page
      title={`Welcome, ${getDisplayName(profile) || 'User'}`}
      showOrganizationSelector={showMaturities}
      onSelectOrganization={selectOrg}
    >
      {isAdmin && securitySection}

      {/* Admin View */}
      {isAdmin && (
        <div className="grid gap-4 md:grid-cols-3">
          <SectionCard
            title={
              <span className="inline-flex items-center">
                <ShieldCheck className="mr-2 h-4 w-4" />
                Admin shortcuts
              </span>
            }
          >
            <div className="grid gap-2">
              <Link to="/admin?tab=import" className="inline-flex items-center linkCls">
                <Upload className="mr-2 h-4 w-4" />
                Upload data files
              </Link>

              <Link to="/admin?tab=orgs" className="inline-flex items-center linkCls">
                <Upload className="mr-2 h-4 w-4" />
                Create org
              </Link>

              <Link to="/admin?tab=views" className="inline-flex items-center linkCls">
                <KeyRound className="mr-2 h-4 w-4" />
                Manage org access
              </Link>

              <Link to="/admin?tab=users" className="inline-flex items-center linkCls">
                <Users className="mr-2 h-4 w-4" />
                Manage user apps
              </Link>
            </div>
          </SectionCard>

          <SectionCard title="Operational snapshot" className="md:col-span-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Metric
                label="Missing securities"
                value={adminSnap.missingSecurities}
                nonzeroTone="red"
              />

              <Metric
                label="Failed uploads (30d)"
                value={adminSnap.failedUploads}
                nonzeroTone="red"
              />

              <Metric
                label="New users (30d)"
                value={adminSnap.newUsers}
                nonzeroTone="amber"
              />

              <Metric
                label="User invitations (30d)"
                value={adminSnap.userInvites}
                nonzeroTone="amber"
              />

              <Metric
                label="Users without 2FA"
                value={adminSnap.usersWithout2FA}
                nonzeroTone="amber"
              />
            </div>
          </SectionCard>
        </div>
      )}

      {/* Org Apps */}
      {!isAdmin && (
        <>
          <SectionCard title="Apps">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {APP_CATALOG.map((app) =>
                app.is_paid && !hasDashboardAppAccess(app.slug) ? (
                  <RequestTile key={app.slug} app={app} />
                ) : (
                  <AppTile
                    key={app.slug}
                    app={app}
                    to={getDashboardAppDestination(app, appSlugs)}
                  />
                )
              )}
            </div>
          </SectionCard>

          {securitySection}

          {showMaturities && (
            <SectionCard
              title={<span className="inline-flex items-center"><CalendarDays className="mr-2 h-4 w-4" />Calendar Reminders</span>}
              headerAction={(
                <div className="flex flex-wrap items-center justify-end gap-3">
                  <CalendarDayControl
                    label="Lookback (days)"
                    value={calendarLookbackDays}
                    onChange={setCalendarLookbackDays}
                  />
                  <CalendarDayControl
                    label="Lookahead (days)"
                    value={calendarLookaheadDays}
                    onChange={setCalendarLookaheadDays}
                  />
                </div>
              )}
            >
              {!selectedOrg ? (
                <p className="text-sm text-muted">Select an organization to view calendar reminders.</p>
              ) : maturityError ? (
                <p className="text-sm text-muted">Calendar events could not be loaded right now.</p>
              ) : maturitiesLoading ? (
                <p className="text-sm text-muted">Loading calendar events…</p>
              ) : calendarEvents.length ? (
                <CalendarEventsTable events={calendarEvents} />
              ) : (
                <p className="text-sm text-muted">No calendar events right now.</p>
              )}
            </SectionCard>
          )}
          <KnowledgeBaseSection />
        </>
      )}

      {/* Platform Admin Version Info */}
      {isAdmin && (
        <SectionCard title="System Information">
          <VersionInfo />
        </SectionCard>
      )}
    </Page>
  );
}

function CalendarDayControl({ label, value, onChange }) {
  return (
    <label className="inline-flex items-center gap-2 whitespace-nowrap text-xs font-medium text-muted">
      <span>{label}</span>
      <input
        type="number"
        min="0"
        max="365"
        step="1"
        value={value}
        onChange={(event) => {
          const nextValue = Number(event.target.value);
          onChange(Number.isFinite(nextValue)
            ? Math.max(0, Math.min(365, Math.trunc(nextValue)))
            : 0);
        }}
        className="h-8 w-16 rounded-md border border-input bg-background px-2 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
      />
    </label>
  );
}

/* ---------------------- Subsections ---------------------- */

function KnowledgeBaseSection() {
  const items = [
    {
      label: 'TradeTracker guide',
      to: '/kb/trade-tracker',
      icon: <LineChart className="h-4 w-4" />,
    },
    {
      label: 'DebtStudio guide',
      to: '/kb/debt-studio',
      icon: <BookOpenCheck className="h-4 w-4" />,
    },
    {
      label: 'FundPlanner guide',
      to: '/kb/fund-planner',
      icon: <WalletCards className="h-4 w-4" />,
    },
    {
      label: 'CapitalFlow guide',
      to: '/kb/capital-flow',
      icon: <Repeat className="h-4 w-4" />,
    },
    {
      label: 'FAQs',
      to: '/kb/faq',
      icon: <HelpCircle className="h-4 w-4" />,
    },
    {
      label: 'Contact support',
      to: '/kb/support',
      icon: <LifeBuoy className="h-4 w-4" />,
    },
  ];

  return (
    <SectionCard title="Knowledge Base">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            className="card p-4 hover:shadow-md transition"
          >
            <div className="flex items-center gap-2 text-base font-medium">
              {it.icon}
              <span>{it.label}</span>
            </div>
          </Link>
        ))}
      </div>
    </SectionCard>
  );
}

function RequestTile({ app }) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 text-lg font-semibold">
        {app.icon}
        <span>{app.label}</span>
      </div>

      <div className={`text-xs ${muted} mt-1`}>
        {app.blurb}
      </div>
    </div>
  );
}

function AppTile({ app, to }) {
  return (
    <Link
      to={to}
      className="card p-4 hover:shadow-md transition"
    >
      <div className="flex items-center gap-2 text-lg font-medium">
        {app.icon}
        <span>{app.label}</span>
      </div>

      <div className={`text-xs ${muted} mt-1`}>
        Click to open
      </div>
    </Link>
  );
}

const METRIC_TONE_CLASSES = {
  neutral: 'border-border bg-card text-app',
  green: 'border-green-500/40 bg-green-500/15 text-green-800 dark:text-green-200',
  amber: 'border-amber-500/40 bg-amber-500/15 text-amber-800 dark:text-amber-200',
  red: 'border-red-500/40 bg-red-500/15 text-red-800 dark:text-red-200',
};

function Metric({ label, value, nonzeroTone }) {
  const tone = value === null || value === undefined
    ? 'neutral'
    : value > 0
      ? nonzeroTone
      : 'green';

  return (
    <div className={`rounded-lg border p-3 shadow-sm ${METRIC_TONE_CLASSES[tone]}`}>
      <div className="text-xs opacity-80">
        {label}
      </div>

      <div className="text-xl font-semibold">
        {fmtNum(value)}
      </div>
    </div>
  );
}

function fmtNum(n) {
  if (n === null || n === undefined) return '—';
  return Intl.NumberFormat().format(n);
}
